import { useState, useCallback, useEffect, useMemo } from 'react';
import { useCountryRisk, type ForecastHorizon } from '../hooks/useCountryRisk';
import { useMapLayer } from '../hooks/useMapLayer';
import { RISK_COLORS } from '../types';
import type { RiskLevel } from '../types';
import {
  bandKindOf,
  bandRateText,
  fmtIssued,
  hasSeasonalPercentile,
  seasonalComparison,
  SHARE_CAVEAT,
} from '../lib/riskText';
import { RISK_PT } from '../lib/concelhos';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import MapShell from '../components/map/MapShell';
import CountryRiskLayer, { type ViewMode } from '../components/map/CountryRiskLayer';
import RiskLegend from '../components/map/RiskLegend';
import LayerControl from '../components/map/LayerControl';
import ModuleBadge from '../components/layout/ModuleBadge';
import Spinner from '../components/ui/Spinner';
import Alert from '../components/ui/Alert';

interface SelectedCell {
  lat: number;
  lon: number;
  probability: number;
  riskLevel: RiskLevel;
  percentile?: number;
}

const ALL_RISK_LEVELS: RiskLevel[] = ['Low', 'Moderate', 'High', 'Extreme'];

type MobilePanel = 'stats' | 'legend' | null;

const PANEL = 'rounded-xl border border-ink-700 bg-ink-900/85 shadow-2xl backdrop-blur';
const CTRL_LABEL = 'font-mono text-[11px] uppercase tracking-wider text-mist-faint';
const CAPTION = 'mt-2 text-[11px] leading-snug text-mist-faint';
const CHIP = `${PANEL} flex items-center gap-2 px-3 py-2 text-sm font-medium text-mist active:bg-ink-800`;

function toggleClass(active: boolean): string {
  return `flex-1 text-sm font-medium rounded-lg px-3 py-1.5 transition-colors ${
    active ? 'bg-ember text-ink-950' : 'bg-ink-800 text-mist-muted hover:bg-ink-700 hover:text-mist'
  }`;
}

export default function CountryRisk() {
  useDocumentMeta(
    'Mapa nacional de risco de incêndio (1 km) | Tutela IGNIS',
    'Mapa de risco de incêndio para todo o Portugal continental, quadrícula a quadrícula (1 km), atualizado diariamente. Veja hoje e a previsão para amanhã.',
    '/ignis/mapa-nacional',
  );
  const { layer: mapLayer, setLayer: setMapLayer, config: layerConfig } = useMapLayer();
  const [horizon, setHorizon] = useState<ForecastHorizon>('today');
  const [viewMode, setViewMode] = useState<ViewMode>('risk');
  const { data, isLoading, error } = useCountryRisk(undefined, horizon);
  const [selectedCell, setSelectedCell] = useState<SelectedCell | null>(null);
  const [mobilePanel, setMobilePanel] = useState<MobilePanel>(null);
  const [visibleLevels, setVisibleLevels] = useState<Set<RiskLevel>>(
    () => new Set(ALL_RISK_LEVELS),
  );

  // Wording keys on the served model family (W5): share bands for the rebuilt
  // model, the legacy probability wording otherwise, so a rollback stays truthful.
  const share = bandKindOf(data?.model_version) === 'share';
  const dayLabel = horizon === 'today' ? 'hoje' : 'amanhã';
  // The seasonal anomaly was a property of the legacy model; the daily path
  // publishes no percentile, and the view hides itself with it.
  const anomalyAvailable = useMemo(() => hasSeasonalPercentile(data?.cells), [data]);
  useEffect(() => {
    if (!anomalyAvailable && viewMode === 'anomaly') setViewMode('risk');
  }, [anomalyAvailable, viewMode]);

  const handleCellClick = useCallback((lat: number, lon: number, prob: number, risk: RiskLevel, percentile?: number) => {
    setSelectedCell({ lat, lon, probability: prob, riskLevel: risk, percentile });
  }, []);

  const handleToggleLevel = useCallback((level: RiskLevel) => {
    setVisibleLevels((prev) => {
      const next = new Set(prev);
      if (next.has(level)) next.delete(level);
      else next.add(level);
      return next;
    });
  }, []);

  const riskSummary = data?.risk_summary;
  const totalCells = data?.total_cells || 0;
  const issued = data?.prediction_time ? fmtIssued(data.prediction_time) : '';

  const horizonToggle = (
    <div className="flex gap-1">
      <button type="button" onClick={() => setHorizon('today')} className={toggleClass(horizon === 'today')} aria-pressed={horizon === 'today'}>
        Hoje
      </button>
      <button type="button" onClick={() => setHorizon('24h')} className={toggleClass(horizon === '24h')} aria-pressed={horizon === '24h'}>
        Amanhã
      </button>
    </div>
  );

  const viewToggle = anomalyAvailable ? (
    <div className="flex gap-1">
      <button type="button" onClick={() => setViewMode('risk')} className={toggleClass(viewMode === 'risk')} aria-pressed={viewMode === 'risk'}>
        Risco
      </button>
      <button type="button" onClick={() => setViewMode('anomaly')} className={toggleClass(viewMode === 'anomaly')} aria-pressed={viewMode === 'anomaly'}>
        Anomalia
      </button>
    </div>
  ) : null;

  // Horizon explainer: one run a day in share mode (decisions D2 and D5).
  const horizonCaption = share
    ? 'Um mapa por dia, emitido de manhã. Amanhã é o mapa do dia seguinte, da mesma emissão.'
    : 'Que dia estás a ver. Hoje: risco de hoje. Amanhã: previsão a +24h.';
  const issuedLine = data && (
    <p className="mt-1 font-mono text-[11px] text-mist-muted">
      {share ? 'Emitido' : 'Atualizado'} {issued}
      {data.for_date ? ` · mapa para ${data.for_date}` : ''}
    </p>
  );

  // Shared between the desktop top-right panel and the mobile "Resumo" sheet.
  const statsContent = data && (
    <div className="space-y-3 text-mist">
      <div>
        <h3 className="font-display font-bold text-mist">Mapa Nacional de Risco</h3>
        <p className="mt-1 text-sm text-mist-muted">
          <span className="font-mono text-mist">{totalCells.toLocaleString('pt-PT')}</span> células de 1 km.
          Cada célula recebe a sua própria previsão; juntas cobrem Portugal continental.
        </p>
      </div>

      {/* Model explainer */}
      <div className="rounded-lg border border-ink-700 bg-ink-800/60 p-3">
        <div className={CTRL_LABEL}>Modelo</div>
        <p className="mt-1 font-mono text-xs text-ember break-all">{data.model_version}</p>
        {share ? (
          <p className="mt-2 text-[11px] leading-snug text-mist-muted">
            Modelo reconstruído (2026). Parte do histórico de ignições de cada quadrícula e mês e
            aprende, com a meteorologia do dia (IFS, ERA5-Land, índices FWI), o desvio face a esse
            histórico. Uma emissão por dia. As classes são quotas fixas do território (1, 2 e 5 por
            cento), atribuídas por ordem de risco: {SHARE_CAVEAT.toLowerCase()}
          </p>
        ) : (
          <p className="mt-2 text-[11px] leading-snug text-mist-muted">
            Ensemble sazonal: usa o modelo mais adequado a cada época do ano (a primavera e o verão
            ardem de formas diferentes) e calibra as probabilidades mês a mês. Combina dois modelos
            treinados, o be23 e o lst1. É mais fiável ao longo do ano do que um único modelo fixo.
          </p>
        )}
      </div>

      {/* Risk distribution bars */}
      {riskSummary && (
        <div className="space-y-1.5">
          {(['Extreme', 'High', 'Moderate', 'Low'] as RiskLevel[]).map((level) => {
            const count = riskSummary[level] || 0;
            const pct = totalCells > 0 ? (count / totalCells) * 100 : 0;
            return (
              <div key={level} className="flex items-center gap-2 text-xs">
                <div className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: RISK_COLORS[level] }} />
                <span className="w-16 text-mist font-medium">{RISK_PT[level]}</span>
                <div className="flex-1 bg-ink-800 rounded-full h-2">
                  <div
                    className="h-2 rounded-full transition-all"
                    style={{ width: `${Math.max(pct, 0.5)}%`, backgroundColor: RISK_COLORS[level] }}
                  />
                </div>
                <span className="w-20 text-right font-mono text-mist-muted">
                  {count.toLocaleString('pt-PT')} ({pct.toFixed(1)}%)
                </span>
              </div>
            );
          })}
          {share && (
            <p className="text-[11px] leading-snug text-mist-faint">
              As quotas são fixas por construção: o que muda de dia para dia é onde ficam.
            </p>
          )}
        </div>
      )}

      {/* Seasonal anomaly count (legacy model only) */}
      {!share && data.anomaly_count != null && data.anomaly_count > 0 && (
        <div className="flex items-center gap-2 text-xs bg-ember/10 border border-ember/30 rounded-lg px-2 py-1.5">
          <div className="w-3 h-3 rounded-full bg-ember flex-shrink-0" />
          <span className="text-ember font-medium">
            {data.anomaly_count.toLocaleString('pt-PT')} anomalias sazonais
          </span>
          <span className="text-mist-muted">(top 5% para este mês)</span>
        </div>
      )}

      {/* Horizon + view explainers live here on mobile (captions are hidden in the top bar) */}
      <div className="md:hidden text-[11px] leading-snug text-mist-faint space-y-1">
        <p>{horizonCaption}</p>
        {share ? (
          <p>Risco: posição de cada célula na ordenação do dia. {SHARE_CAVEAT}</p>
        ) : (
          <p>Risco: pontuação relativa, onde é mais provável arder comparado com o resto do país.{anomalyAvailable ? ' Anomalia: quão acima do normal para esta época do ano.' : ''}</p>
        )}
      </div>

      {/* Issue time (decision D5: each map says when it was issued) */}
      <div className="text-xs text-mist-faint border-t border-ink-700 pt-2">
        {share ? 'Emitido' : 'Atualizado'}: {issued || 'N/A'}
        {share && data.for_date ? ` · mapa para ${data.for_date}` : ''}
      </div>
    </div>
  );

  const cellCard = selectedCell && (
    <div className={`${PANEL} p-4 text-mist`}>
      <div className="flex justify-between items-start">
        <div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: RISK_COLORS[selectedCell.riskLevel] }} />
            <span className="font-semibold text-sm text-mist">{RISK_PT[selectedCell.riskLevel]}</span>
          </div>
          {share ? (
            <>
              <p className="mt-1.5 text-xs leading-snug text-mist-muted">
                {bandRateText(selectedCell.riskLevel)}
              </p>
              <p className="mt-1 text-[11px] leading-snug text-mist-faint">{SHARE_CAVEAT}</p>
            </>
          ) : (
            <>
              <p className="text-2xl font-bold font-display text-mist mt-1">
                {(selectedCell.probability * 100).toFixed(1)}%
              </p>
              <p className="text-[10px] uppercase tracking-wider text-mist-faint">pontuação de risco</p>
              {(() => {
                const sc = seasonalComparison(selectedCell.percentile);
                if (!sc) return null;
                return (
                  <div className="mt-1.5">
                    <p className={`text-xs font-medium ${sc.anomaly ? 'text-ember' : 'text-mist-muted'}`}>
                      {sc.label}
                    </p>
                    {sc.detail && <p className="text-[11px] text-mist-faint">{sc.detail}</p>}
                  </div>
                );
              })()}
            </>
          )}
          <p className="text-xs text-mist-faint font-mono mt-1">
            {selectedCell.lat.toFixed(4)}, {selectedCell.lon.toFixed(4)}
          </p>
        </div>
        <button onClick={() => setSelectedCell(null)} className="text-mist-faint hover:text-mist">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );

  return (
    <div className="h-[calc(100vh-4rem)] relative">
      <MapShell tileUrl={layerConfig.url} tileAttribution={layerConfig.attribution} tileKey={mapLayer}>
        {data?.cells && (
          <CountryRiskLayer
            cells={data.cells}
            onCellClick={handleCellClick}
            visibleLevels={visibleLevels}
            viewMode={viewMode}
            showProbability={!share}
          />
        )}
      </MapShell>

      {/* Desktop: top-left control column */}
      <div className="absolute top-4 left-4 z-[1000] hidden md:flex flex-col gap-3 w-60">
        <LayerControl currentLayer={mapLayer} onLayerChange={setMapLayer} dark />

        {/* Horizon: which day the forecast is for */}
        <div className={`${PANEL} px-3 py-2.5`}>
          <div className={CTRL_LABEL}>Horizonte</div>
          <div className="mt-1.5">{horizonToggle}</div>
          <p className={CAPTION}>{horizonCaption}</p>
          {issuedLine}
        </div>

        {/* View: risk probability vs seasonal anomaly (legacy model only) */}
        {viewToggle && (
          <div className={`${PANEL} px-3 py-2.5`}>
            <div className={CTRL_LABEL}>Vista</div>
            <div className="mt-1.5">{viewToggle}</div>
            <p className={CAPTION}>
              Risco: pontuação relativa, onde é mais provável arder comparado com o resto do país. Anomalia: quão acima do normal para esta época do ano.
            </p>
          </div>
        )}
      </div>

      {/* Mobile: one compact top bar (no labels/captions; explainers live in the Resumo sheet) */}
      <div className="absolute top-3 inset-x-3 z-[1000] flex gap-2 md:hidden">
        <div className={`${PANEL} flex-1 px-2 py-1.5`}>{horizonToggle}</div>
        {viewToggle && <div className={`${PANEL} flex-1 px-2 py-1.5`}>{viewToggle}</div>}
      </div>

      {/* Desktop: bottom-left legend */}
      <div className="absolute bottom-8 left-4 z-[1000] hidden md:block">
        <RiskLegend
          visibleLevels={visibleLevels}
          onToggle={handleToggleLevel}
          viewMode={viewMode}
          bandKind={share ? 'share' : 'probability'}
          dayLabel={dayLabel}
          dark
        />
      </div>

      {/* Desktop: top-right stats panel */}
      <div className="absolute top-4 right-4 z-[1000] w-80 hidden md:block">
        {isLoading && (
          <div className={`${PANEL} p-4`}>
            <div className="flex items-center gap-3">
              <Spinner />
              <span className="text-mist-muted">A carregar mapa nacional...</span>
            </div>
          </div>
        )}
        {error && <Alert variant="error">Erro ao carregar previsoes nacionais</Alert>}
        {statsContent && !isLoading && <div className={`${PANEL} p-4`}>{statsContent}</div>}
        {cellCard && <div className="mt-3">{cellCard}</div>}
      </div>

      {/* Mobile: loading / error, below the top bar */}
      <div className="absolute top-16 inset-x-3 z-[1000] md:hidden">
        {isLoading && (
          <div className={`${PANEL} p-3`}>
            <div className="flex items-center gap-3">
              <Spinner />
              <span className="text-sm text-mist-muted">A carregar mapa nacional...</span>
            </div>
          </div>
        )}
        {error && <Alert variant="error">Erro ao carregar previsoes nacionais</Alert>}
      </div>

      {/* Mobile: selected cell card above the bottom chips */}
      {cellCard && (
        <div className="absolute inset-x-3 bottom-20 z-[1010] md:hidden">{cellCard}</div>
      )}

      {/* Mobile: bottom chips (legend + summary) */}
      <div className="absolute bottom-6 left-3 z-[1000] flex gap-2 md:hidden">
        <button type="button" className={CHIP} onClick={() => setMobilePanel('legend')}>
          <span className="flex gap-0.5">
            {ALL_RISK_LEVELS.map((level) => (
              <span key={level} className="w-2 h-2 rounded-full" style={{ backgroundColor: RISK_COLORS[level] }} />
            ))}
          </span>
          Legenda
        </button>
        <button type="button" className={CHIP} onClick={() => setMobilePanel('stats')}>
          Resumo
        </button>
      </div>

      {/* Mobile: bottom sheet for legend / summary */}
      {mobilePanel && (
        <div className="md:hidden">
          <button
            type="button"
            aria-label="Fechar painel"
            className="absolute inset-0 z-[1090] bg-ink-950/60"
            onClick={() => setMobilePanel(null)}
          />
          <div className="absolute inset-x-0 bottom-0 z-[1100] max-h-[70vh] overflow-y-auto rounded-t-2xl border-t border-ink-700 bg-ink-950/95 backdrop-blur p-4 pb-6">
            <div className="flex items-center justify-between mb-3">
              <span className={CTRL_LABEL}>{mobilePanel === 'legend' ? 'Legenda' : 'Resumo'}</span>
              <button onClick={() => setMobilePanel(null)} className="text-mist-faint hover:text-mist p-1" aria-label="Fechar">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            {mobilePanel === 'legend' ? (
              <div className="space-y-3">
                <RiskLegend
                  visibleLevels={visibleLevels}
                  onToggle={handleToggleLevel}
                  viewMode={viewMode}
                  bandKind={share ? 'share' : 'probability'}
                  dayLabel={dayLabel}
                  dark
                />
                <div>
                  <div className={`${CTRL_LABEL} mb-1.5`}>Mapa base</div>
                  <LayerControl currentLayer={mapLayer} onLayerChange={setMapLayer} dark />
                </div>
              </div>
            ) : (
              statsContent || <p className="text-sm text-mist-muted">Sem dados.</p>
            )}
          </div>
        </div>
      )}

      <div className="hidden md:block">
        <ModuleBadge label="Mapa Nacional" dark />
      </div>
    </div>
  );
}
